import { useState } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/da2399tv7gurs51ef580";

const contactItems = [
  { icon: "ri-mail-fill", label: "商务合作邮箱", value: "business@fanmugua.readdy.site" },
  { icon: "ri-customer-service-2-line", label: "读者反馈", value: "feedback@fanmugua.readdy.site" },
  { icon: "ri-time-line", label: "响应时间", value: "工作日 9:00 - 18:00 回复" },
  { icon: "ri-wechat-2-line", label: "官方微信", value: "关注「番木瓜漫画」公众号" },
];

type Status = "idle" | "submitting" | "success";

export default function Contact() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("company_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }
    formData.delete("company_alt");

    setStatus("submitting");
    setFormError("");

    try {
      const res = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || (parsed as { message?: string })?.message || parsed?.meta?.detail || responseText;

      if (res.ok && parsed?.code === "OK") {
        setStatus("success");
        form.reset();
      } else {
        setStatus("idle");
        setFormError(
          typeof serverMsg === "string" && serverMsg.trim()
            ? serverMsg
            : "提交失败，请稍后重试。"
        );
      }
    } catch {
      setStatus("idle");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  }

  return (
    <section id="contact" className="bg-background-50 py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <span className="inline-flex items-center gap-2 rounded-full bg-secondary-100 px-3 py-1 text-sm font-semibold text-secondary-800">
              <i className="ri-contacts-book-line"></i> 联系我们
            </span>
            <h2 className="mt-3 font-heading text-3xl text-foreground-950 md:text-4xl">有问题或想合作？聊聊吧</h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">
              无论是作品建议、追更问题、版权合作还是商务洽谈，都欢迎通过下方表单或联系方式与我们沟通，我们会尽快回复。
            </p>

            <ul className="mt-8 space-y-4">
              {contactItems.map((item) => (
                <li key={item.label} className="flex items-center gap-4 rounded-2xl bg-background-100 p-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-700">
                    <i className={`${item.icon} text-lg`}></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">{item.label}</p>
                    <p className="mt-0.5 text-sm font-semibold text-foreground-950">{item.value}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <form
              onSubmit={handleSubmit}
              data-readdy-form="da2399tv7gurs51ef580"
              className="rounded-3xl bg-background-100 p-6 md:p-8"
            >
              <h3 className="text-lg font-bold text-foreground-950">给我们留言</h3>
              <p className="mt-1 text-sm text-foreground-500">请填写以下信息，我们会尽快与你联系。</p>

              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="ct-name" className="text-sm font-semibold text-foreground-800">
                    你的称呼
                  </label>
                  <input
                    id="ct-name"
                    name="name"
                    type="text"
                    required
                    placeholder="例如：小林"
                    className="w-full rounded-lg bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none ring-1 ring-background-200/80 transition focus:ring-2 focus:ring-primary-400"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="ct-email" className="text-sm font-semibold text-foreground-800">
                    联系邮箱
                  </label>
                  <input
                    id="ct-email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="w-full rounded-lg bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none ring-1 ring-background-200/80 transition focus:ring-2 focus:ring-primary-400"
                  />
                </div>
              </div>

              <div className="mt-4 flex flex-col gap-1.5">
                <label htmlFor="ct-topic" className="text-sm font-semibold text-foreground-800">
                  反馈类型
                </label>
                <select
                  id="ct-topic"
                  name="topic"
                  className="w-full rounded-lg bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none ring-1 ring-background-200/80 transition focus:ring-2 focus:ring-primary-400"
                  defaultValue="追更问题"
                >
                  <option>追更问题</option>
                  <option>作品建议</option>
                  <option>版权合作</option>
                  <option>商务洽谈</option>
                  <option>其他</option>
                </select>
              </div>

              <div className="mt-4 flex flex-col gap-1.5">
                <label htmlFor="ct-msg" className="text-sm font-semibold text-foreground-800">
                  留言内容
                </label>
                <textarea
                  id="ct-msg"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  placeholder="请描述你的问题或合作意向（最多 500 字）"
                  className="w-full resize-none rounded-lg bg-background-50 px-4 py-3 text-sm text-foreground-950 outline-none ring-1 ring-background-200/80 transition focus:ring-2 focus:ring-primary-400"
                ></textarea>
              </div>

              {/* 防垃圾字段 */}
              <input
                type="text"
                name="company_alt"
                className="contact-guard-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              {formError && (
                <p role="alert" className="mt-4 rounded-lg bg-accent-100 px-4 py-3 text-sm text-accent-700">
                  {formError}
                </p>
              )}
              {status === "success" && (
                <p role="status" className="mt-4 rounded-lg bg-secondary-100 px-4 py-3 text-sm font-semibold text-secondary-800">
                  <i className="ri-checkbox-circle-fill mr-1"></i> 提交成功！感谢你的反馈，我们会尽快与你联系。
                </p>
              )}

              <button
                type="submit"
                disabled={status === "submitting"}
                className="mt-6 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {status === "submitting" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i> 提交中…
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill"></i> 提交留言
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}